import React, { useState, useEffect, useMemo, memo, useCallback } from 'react'
import * as styles from './crudItems.module.scss'
import * as stylesCrudForm from '../crud/crudForm.module.scss'
import { Col, Row } from 'react-grid-system'
import { Actions } from '../../../constants/tables'
import { Button, Modal, Alert, Tabs } from '../../common'
import {
   Details,
   Inventory,
   Images,
   Other,
   Rental,
   Notes,
   History,
   Map,
   Tasks,
} from './'
import { useSettings } from '../../../hooks'

import { Severity, ToolTypes } from '../../../types'
import useImageMatch from '../../../hooks/useImageMatch/useImageMatch'
import {
   CatalogueImage,
   ImageMatchPayload,
   UploadMatchedImagesPayload,
} from '../../../hooks/useImageMatch/types'
import { CrudItemsParentView } from '../../../constants/crudItems'
import { randomString } from '../../../utils/functions'
import { crudItemTabs, TabStatus } from './constants'
import { CrudItemsProps } from './types'
import { InventoryWarehouse } from './inventory/types'
import {
   arraysAreEqual,
   convertInventoryWarehouseToItemWarehouse,
   convertItemWarehouseToInventoryWarehouse,
   sortInventoryWarehouses,
} from './inventory/helpers'
import { isAfter, parseISO } from 'date-fns'

const CrudItems = ({
   isNewItemNote,
   setIsNewItemNote,
   postItem,
   itemImages,
   setItemImages,
   changePosition,
   deleteImage,
   isPositionSuccess,
   setTabStatus,
   tabStatus,
   activeValue,
   isLoading,
   itemTypes,
   categories,
   manufacturers,
   locations,
   conditions,
   itemNotesData,
   itemHistoryData,
   handleAction,
   closeModal,
   itemAction,
   itemId,
   isModalVisible,
   setItemForm,
   itemForm,
   resetRef,
   getItemView,
   refetchNoteTableData,
   handleAddOrRemoveFromCart,
   isItemAlreadyAddedToCart,
   isEmployeeUser,
   isViewOnlyUser,
   parentPageView = null,
   parentTaskListDetails,
   handleWorkflowAction,
   createItemSuccess,
   createItemResponse,
   isAdminCompanyUser,
   refetchItemTableData,
   handleAdjustClick,
   handleAddToKitClick,
   // Necessary to fix bug 24241 due to a complex useEffect chain negating copied warehouses
   copiedWarehouses,
}: CrudItemsProps) => {
   const { settings } = useSettings()
   const [toggleValue, setToggleValue] = useState(
      Boolean(itemForm.IsRentalTool)
   ) // changed from item settings
   const [isSecondaryModalVisible, setIsSecondaryModalVisible] =
      useState<boolean>(false)
   const [titleErrorMessage, setTitleErrorMessage] = useState<string | null>(
      null
   )
   // Necessary to give all information to copied item
   const [inventoryWarehouses, setInventoryWarehouses] = useState<
      InventoryWarehouse[]
   >(copiedWarehouses ? copiedWarehouses.map((item) => ({...item, ...convertItemWarehouseToInventoryWarehouse(item)})) : [])

   const isItemCurrentlyInActionCart = useMemo(() => {
      if (isItemAlreadyAddedToCart) {
         return isItemAlreadyAddedToCart(itemForm.localUniqueId)
      }
      return false
   }, [itemForm])

   const isQuantityOrConsumableToolType = useMemo(
      () =>
         itemForm.ToolType == ToolTypes['Quantity Item'] ||
         itemForm.ToolType == ToolTypes['Consumable'],
      [itemForm]
   )

   const isQuantityItem = useMemo(
      () => itemForm.ToolType == ToolTypes['Quantity Item'],
      [itemForm]
   )

   useEffect(() => {
      // Kit and Standard Items
      if (itemForm?.IsKitItem || !isQuantityOrConsumableToolType) {
         const warehousesToSet: InventoryWarehouse[] = itemForm?.Warehouse
            ? [convertItemWarehouseToInventoryWarehouse(itemForm.Warehouse)]
            : []
         // only set the initial warehouse here to prevent subsequent re-renders
         if (
            inventoryWarehouses?.length === 0 &&
            inventoryWarehouses[0]?.id !==
               itemForm?.Warehouse?.WarehouseLocationId
         ) {
            setInventoryWarehouses(sortInventoryWarehouses(warehousesToSet))
         }
      } else {
         // Quantity and Consumable Items
         if (!copiedWarehouses) {
            const warehousesToSet: InventoryWarehouse[] =
               itemForm?.Warehouses?.length > 0
                  ? itemForm.Warehouses.map((warehouse) =>
                     convertItemWarehouseToInventoryWarehouse(warehouse)
                  )
                  : []
            setInventoryWarehouses(sortInventoryWarehouses(warehousesToSet))
         }
      }
   }, [isQuantityOrConsumableToolType, itemForm?.IsKitItem])

   // what on earth does this do
   useEffect(() => {
      const currentWarehouses =
         inventoryWarehouses?.length > 0
            ? inventoryWarehouses.map((warehouse) =>
                 copiedWarehouses ? warehouse : convertInventoryWarehouseToItemWarehouse(warehouse)
              )
            : []
      const itemWarehouses = isQuantityOrConsumableToolType
         ? itemForm?.Warehouses
         : [itemForm?.Warehouse]
      if (
         !arraysAreEqual(itemWarehouses ?? [], currentWarehouses)
      ) {
         updateForm('Warehouses', currentWarehouses)
      }
   }, [inventoryWarehouses, isQuantityOrConsumableToolType, itemAction])

   const closeSecondaryModal = () => {
      setIsSecondaryModalVisible(false)
      resetRef?.current?.click()
   }

   const closeAllModals = () => {
      setIsSecondaryModalVisible(false)
      resetRef?.current?.click()
      closeModal()
   }

   const closeModalAndRefresh = () => {
      closeAllModals()
      refetchItemTableData()
   }

   const handleToggle = () => {
      setItemForm({
         ...itemForm,
         IsRentalTool: !toggleValue,
      })
      setToggleValue(!toggleValue)
   }

   const [updateAction, setUpdateAction] = useState<any>({})

   const updateForm = (key, value) => {
      setItemForm({
         ...itemForm,
         [key]: value,
      })
      setUpdateAction({ key, value, itemForm })
   }

   const updateFormSelect = (obj: {}) => {
      setUpdateAction({ obj })
   }

   useEffect(() => {
      const { key, value, obj } = updateAction
      if (!updateAction.hasOwnProperty('key')) {
         setItemForm((prevState) => ({
            ...prevState,
            ...obj,
         }))
      }

      if (
         (updateAction.hasOwnProperty('key') &&
            key === 'Title' &&
            value !== '') ||
         key !== 'Title'
      ) {
         setTitleErrorMessage(null)
         // 24241 warehouses array being lost here
         setItemForm((prevState) => ({
            ...prevState,
            ...obj,
            [key]: value,
         }))
      } else {
         setTitleErrorMessage('Title field is required')
      }
   }, [updateAction])

   const { PictureURLs } = itemForm

   const [shadowPictureUrlsList, setShadowPictureUrlsList] = useState(
      PictureURLs ?? []
   ) // used to track image urls

   // START: Drag image
   const onDragEnd = (result: {
      destination: { index: number }
      source: { index: number }
   }) => {
      if (!result.destination) {
         return
      }

      const _items = reorder(
         itemImages,
         result.source.index,
         result.destination.index
      )

      const _urls = reorder(
         shadowPictureUrlsList,
         result.source.index,
         result.destination.index
      )

      setShadowPictureUrlsList(_urls)
      setItemImages(_items)
   }
   // END: Drag image

   const resetImageShuffle = () => {
      // setIsImageAdding(false)
      setUploadImageDisabled(false)
   }

   const handleShuffle = () => {
      getItemView({
         Id: itemForm.Id,
         ToolLoanId: itemForm.ToolLoanId,
         KitId: itemForm.KitId,
         toolWarehouseId: itemForm.toolWarehouseId,
      })
      setTimeout(resetImageShuffle, 500)
   }

   useEffect(() => {
      if (itemImages.length && isPositionSuccess === true) {
         // setIsImageAdding(true)
         setUploadImageDisabled(true)
         handleShuffle()
      }
   }, [isPositionSuccess])

   const [deleteImageData, setDeleteImageData] = useState([])
   const [existingPositions] = useState(
      PictureURLs?.map((url) => url.Position) ?? []
   )

   const removeItemImage = (id: number, index: number) => {
      const deleteByPosition = itemImages
         .map((image) => {
            if (image.Id === id) return image.OriginalPosition
         })
         .filter(Number)

      setDeleteImageData([
         ...deleteImageData,
         deleteByPosition.length !== 0 ? deleteByPosition : [0],
      ])

      const updatedItemImages = itemImages
         .filter((item) => item.Id !== id)
         .map((image, index) => {
            // update image positions after removing
            image.Position = index
            return image
         })

      const updatedUrls = shadowPictureUrlsList
         .filter((url, urlIndex) => urlIndex !== index)
         .map((url, urlIndex) => {
            url.Position = urlIndex
            if (urlIndex === 0) {
               url.IsPrimary = true
            }
            return url
         })

      const arr = actionList.filter((el) => {
         return id !== el.uiId
      })

      setActionList(arr)
      setItemImages(updatedItemImages)
      setShadowPictureUrlsList(updatedUrls)
   }

   const reorder = (
      list: Iterable<any> | ArrayLike<any>,
      startIndex: number,
      endIndex: number
   ) => {
      const result = Array.from(list)
      const [removed] = result.splice(startIndex, 1)
      result.splice(endIndex, 0, removed)
      const processed = result.map((item, index) => {
         return {
            ...item,
            Position: index,
         }
      })
      return processed
   }

   const [actionList, setActionList] = useState<any[]>([{}])

   const onImageUpload = (
      file: Blob,
      newFile: Blob,
      mimeType: string,
      imageUrl: string
   ) => {
      const nextItem = `item-${itemImages.length + 1}-${randomString()}`
      const addedImage = {
         justAdded: true,
         Id: nextItem,
         Toolid: itemForm.Id,
         Position: itemImages.length,
         FullUrl: imageUrl,
         PictureUrl: imageUrl,
         MimeType: mimeType,
         ThumbnailUrl: imageUrl,
         'File[0]': file,
         'File[1]': newFile,
         IsMatchedImage: false,
      }
      setItemImages((prevState) => [...prevState, addedImage])
   }

   const [uploadImageDisabled, setUploadImageDisabled] =
      useState<boolean>(false)

   useEffect(() => {
      setItemForm({
         ...itemForm,
         itemImages: remapId(itemImages),
      })
   }, [itemImages])

   const remapId = (arr: []) => {
      return arr.map((item: {}, index: number) => {
         return {
            ...item,
            Id: index,
            Position: index,
         }
      })
   }

   const handleSecondaryModal = () => {
      closeAllModals()
   }

   useEffect(() => {
      // handle existing images on load
      if (PictureURLs && !itemForm.copyId) {
         const existingImages = []
         PictureURLs.map((obj) => {
            const newId = `item-${itemImages.length + 1}-${randomString()}`
            const imageToAdd = {
               ...obj,
               Id: newId,
               OriginalId: obj.Id,
               OriginalPosition: obj.Position,
            }
            existingImages.push(imageToAdd)
         })
         setItemImages(existingImages)
      }
   }, [PictureURLs])

   const removeImage = () => {
      const toolId = itemForm.Id
      const deleteImageDataFlat = deleteImageData.flat()
      deleteImageDataFlat.forEach((position) => {
         deleteImage({ toolId, position: existingPositions[position] })
      })
   }

   const handleImageAdditionsAndDeletions = () => {
      // if item wasn't copied then update positions
      if (!itemForm.copyId) {
         if (shadowPictureUrlsList.length && itemForm.Id) {
            const positionsToUpdate = shadowPictureUrlsList.map((url) => ({
               Id: url.Id,
               Position: url.Position,
            }))
            changePosition({
               toolId: itemForm.Id,
               images: positionsToUpdate,
            })
         }

         // if any image deleted remove
         if (deleteImageData.length) {
            removeImage()
         }

         // if item already existed then add any added images
         if (itemForm.Id) {
            const addedImages = itemImages.filter((image) => image.justAdded)
            addedImages?.length > 0 &&
               addedImages.forEach((img) =>
                  postItem(
                     {
                        Toolid: itemForm.Id ?? null,
                        Position: img.Position,
                        'File[0]': img['File[0]'],
                        'File[1]': img['File[1]'],
                     },
                     itemForm.Id
                  )
               )
         }
      }
   }

   const crudItemsPrimaryButtonMessage = useMemo(() => {
      if (itemAction !== Actions.Readonly) {
         return 'Save'
      } else if (
         itemAction === Actions.Readonly &&
         parentPageView === CrudItemsParentView.Actions &&
         isItemCurrentlyInActionCart
      ) {
         return 'Remove From Cart'
      } else if (
         itemAction === Actions.Readonly &&
         parentPageView === CrudItemsParentView.Actions &&
         !isItemCurrentlyInActionCart
      ) {
         return 'Add to Cart'
      } else {
         return 'Save'
      }
   }, [itemForm])

   // Image gallery / viewer functionality
   const [openImageGallery, setOpenImageGallery] = useState<boolean>(false)

   const imagesForGallery = useMemo(
      () =>
         itemImages.map((img) => ({ src: img.FullUrl ?? img.PictureFullURL })),
      [itemImages]
   )

   const allowViewGallery = useMemo(
      () => imagesForGallery?.length > 0 && itemAction !== Actions.Readonly,
      [imagesForGallery, itemAction]
   )

   const viewGalleryTooltip = useMemo(
      () => (allowViewGallery ? '' : 'Item has no images to view'),
      [allowViewGallery]
   )

   const handleViewGalleryClick = () => setOpenImageGallery(!openImageGallery)

   // Image match functionality
   const {
      imageMatch,
      imageMatchData,
      imageMatchError,
      isImageMatchLoading,
      isImageMatchError,
      isImageMatchSuccess,
      uploadMatchedImages,
      isUploadImagesSuccess,
   } = useImageMatch()

   const [imageMatchModalOpen, setImageMatchModalOpen] =
      useState<boolean>(false)
   const [matchedImages, setMatchedImages] = useState<CatalogueImage[]>([])

   useEffect(() => {
      if (isImageMatchLoading) {
         setImageMatchModalOpen(true)
      }
   }, [isImageMatchLoading])

   useEffect(() => {
      if (imageMatchData && imageMatchData?.CatalogueImages?.length > 0) {
         setMatchedImages(imageMatchData.CatalogueImages)
      }
   }, [imageMatchData])

   const handleImageMatchClick = () => {
      const payload: ImageMatchPayload = {
         Manufacturer: itemForm?.Manufacturer,
         ManufacturerID: Number(itemForm?.ManufacturerId),
         ModelNumber: itemForm?.ModelNumber,
      }
      imageMatch(payload)
   }

   const handleImageSelection = (selectedImage: CatalogueImage) => {
      const updatedImages = matchedImages.map((image) =>
         image.PictureFullURL === selectedImage.PictureFullURL &&
         image.Position === selectedImage.Position
            ? {
                 ...image,
                 selected: !image?.selected,
              }
            : image
      )
      setMatchedImages(updatedImages)
   }

   const convertMatchedImageToLocal = (
      image: CatalogueImage,
      index: number
   ) => ({
      ...image,
      Id: `item-${itemImages.length + 1}-${randomString()}`,
      Toolid: itemForm.Id,
      Position: itemImages.length + index,
      FullUrl: image.PictureFullURL,
      IsMatchedImage: true,
   })

   const handleAddMatchedImages = () => {
      const imagesToAdd = matchedImages
         .filter((image) => image.selected)
         .map((image, index) => convertMatchedImageToLocal(image, index))
      const updatedImages = [...itemImages, ...imagesToAdd]
      setItemImages(updatedImages)
      setMatchedImages([])
      setImageMatchModalOpen(false)
   }

   const uploadCatalogueImages = (
      toolId?: string,
      closeIfNoImages?: boolean
   ) => {
      const imagesToUpload: CatalogueImage[] = itemImages?.filter(
         (img: CatalogueImage) => img?.IsMatchedImage
      )
      if (imagesToUpload?.length > 0) {
         const reqPayload: UploadMatchedImagesPayload = {
            toolId: toolId,
            Manufacturer: itemForm?.Manufacturer,
            ManufacturerID: Number(itemForm?.ManufacturerId),
            ModelNumber: itemForm?.ModelNumber,
            selectedImages: imagesToUpload,
         }
         uploadMatchedImages(reqPayload)
      }
      if (imagesToUpload?.length === 0 && closeIfNoImages) {
         resetRef?.current?.click()
         closeModal()
      }
   }

   const handleSave = () => {
      if (
         !itemForm?.Title ||
         itemForm?.Title?.trim().length === 0 ||
         itemForm?.Title?.length > 100
      ) {
         const message =
            itemForm?.Title?.length > 100
               ? 'Title field cannot exceed 100 characters'
               : 'Title field is required'
         return setTitleErrorMessage(message)
      }
      handleImageAdditionsAndDeletions()

      if (itemAction !== Actions.Add && itemAction !== Actions.Copy) {
         uploadCatalogueImages(itemForm?.Id)
      }
      handleAction(itemAction, itemId, itemForm)

      if (itemAction !== Actions.Add && itemAction !== Actions.Copy) {
         resetRef?.current?.click()
         closeModal()
      }
   }

   useEffect(() => {
      if (createItemSuccess && createItemResponse) {
         const createdItemId = createItemResponse?.data?.Data?.Id
         uploadCatalogueImages(createdItemId, true)
      }
   }, [createItemSuccess, createItemResponse])

   useEffect(() => {
      if (isUploadImagesSuccess && refetchItemTableData) {
         // need to refetch item on upload matched images success before closing modal
         refetchItemTableData()
         resetRef?.current?.click()
         closeModal()
      }
   }, [isUploadImagesSuccess, refetchItemTableData])

   const hasManufacturerAndModelNumber = useMemo(
      () =>
         itemForm?.Manufacturer !== '' &&
         itemForm?.ManufacturerId !== 132 &&
         itemForm?.ModelNumber &&
         itemForm?.ModelNumber !== '',
      [itemForm]
   )

   const imageLimitReached = useMemo(
      () => itemImages?.length >= 5,
      [itemImages]
   )

   const imageMatchIsTertiary = useMemo(
      () =>
         itemAction === Actions.Readonly ||
         imageMatchModalOpen ||
         imageLimitReached ||
         !hasManufacturerAndModelNumber,
      [
         itemAction,
         imageMatchModalOpen,
         imageLimitReached,
         hasManufacturerAndModelNumber,
      ]
   )

   const imageMatchIsDisabled = useMemo(
      () =>
         itemAction === Actions.Readonly ||
         imageLimitReached ||
         !hasManufacturerAndModelNumber,
      [itemAction, imageLimitReached, hasManufacturerAndModelNumber]
   )

   const imageMatchTooltip = useMemo(() => {
      if (!hasManufacturerAndModelNumber) {
         return 'Please add a manufacturer and model number to the item, to run Image Match'
      } else if (imageLimitReached) {
         return 'Maximum 5 images allowed. Delete one to run Image Match'
      } else {
         return ''
      }
   }, [imageLimitReached, hasManufacturerAndModelNumber])

   const maxAllowedImages = useMemo(
      () => 5 - (itemImages?.length ?? 0),
      [itemImages]
   )

   const imageMatchErrorMessage = useMemo(() => {
      if (isImageMatchError && imageMatchError) {
         return imageMatchError
      }
      if (isImageMatchSuccess && matchedImages.length === 0) {
         return 'No Images were matched to this item.'
      }
      return null
   }, [isImageMatchError, imageMatchError, matchedImages, isImageMatchSuccess])

   const isAddOrCopy = useMemo(
      () => itemAction === Actions.Add || itemAction === Actions.Copy,
      [itemAction]
   )

   const isReadOnly = useMemo(
      () =>
         (isEmployeeUser && itemAction !== Actions.Add) ||
         itemAction === Actions.Readonly ||
         isViewOnlyUser,
      [isEmployeeUser, isViewOnlyUser, itemAction]
   )

   const hasOverdueNotes = useMemo(() => {
      return itemNotesData?.some(
         (note) =>
            !note.Completed &&
            note.DueDate &&
            isAfter(new Date(), parseISO(note.DueDate))
      )
   }, [itemNotesData])

   const shouldHideTab = useCallback(
      (item: { label: string; status: TabStatus }) => {
         switch (item.label) {
            // hide inventory tab if locations are off or the item is not available
            case 'Inventory': {
               // hide tab if quantity item thats a kit or is not available
               if (isQuantityOrConsumableToolType) {
                  return (
                     itemForm?.IsKitItem ||
                     (itemForm?.StatusDesc !== 'Available' && !isAddOrCopy)
                  )
               }
               // otherwise show the tab
               return false
            }
            // hide tasks if new item or not on the items page
            case 'Tasks':
               return (
                  isAddOrCopy ||
                  (parentPageView !== CrudItemsParentView.Items &&
                     parentPageView !== CrudItemsParentView.Task_Item &&
                     parentPageView !== CrudItemsParentView.Audits)
               )
            // hide these tabs if creating a new item
            case 'Notes':
            case 'History':
               return isAddOrCopy
            // otherwise show the tab
            default:
               return false
         }
      },
      [isAddOrCopy, isQuantityOrConsumableToolType, itemForm, parentPageView]
   )

   const tabItems = useMemo(
      () =>
         crudItemTabs?.map((tab) => ({
            id: tab.status,
            label: tab.label,
            isHidden: shouldHideTab(tab),
            isDisabled: isLoading,
            showCircle:
               (tab.label === 'Notes' && hasOverdueNotes) ||
               (tab.label === 'Tasks' && itemForm?.Notifications > 0),
         })),
      [crudItemTabs, shouldHideTab, isLoading, hasOverdueNotes]
   )

   const onTabClick = useCallback(
      (tabId: TabStatus) => setTabStatus(tabId),
      [setTabStatus]
   )

   return (
      <div className={styles.crudItems}>
         <Tabs
            tabItems={tabItems}
            onTabClick={onTabClick}
            activeTabId={tabStatus}
         />

         <hr className={styles.divider} />

         {/*Item Details*/}
         {tabStatus === TabStatus.DETAILS && (
            <Details
               activeValue={activeValue}
               isQuantityOrConsumableToolType={isQuantityOrConsumableToolType}
               isQuantityItem={isQuantityItem}
               itemTypes={itemTypes}
               itemForm={itemForm}
               itemAction={itemAction}
               manufacturers={manufacturers}
               categories={categories}
               updateForm={updateForm}
               updateFormSelect={updateFormSelect}
               titleError={titleErrorMessage}
               showEditItems={
                  itemAction === Actions.Edit || itemAction === Actions.Readonly
               }
               settings={settings}
               isReadOnly={isReadOnly}
               imagesForGallery={imagesForGallery}
               isEmployeeUser={isEmployeeUser}
            />
         )}
         {/* Inventory Tab */}
         {tabStatus === TabStatus.INVENTORY && isModalVisible && (
            <Inventory
               itemForm={itemForm}
               warehouseOptions={locations}
               isCopyAction={itemAction === Actions.Copy}
               isNewItem={itemAction === Actions.Add}
               handleWorkflowAction={handleWorkflowAction}
               handleAdjustClick={handleAdjustClick}
               selectedWarehouses={inventoryWarehouses}
               setSelectedWarehouses={setInventoryWarehouses}
               isQuantityOrConsumableToolType={isQuantityOrConsumableToolType}
               permissions={itemForm.Permissions ?? []}
               isReadonly={isReadOnly || itemForm?.IsKitItem}
               handleAddToKitClick={handleAddToKitClick}
            />
         )}
         {/* Images Tab */}
         {tabStatus === TabStatus.IMAGES && (
            <Images
               isReadonly={isReadOnly}
               onDragEnd={onDragEnd}
               itemImages={itemImages}
               removeItemImage={removeItemImage}
               imageLimitReached={imageLimitReached}
               matchedImages={matchedImages}
               uploadImageDisabled={uploadImageDisabled}
               onImageUpload={onImageUpload}
               allowViewGallery={allowViewGallery}
               handleViewGalleryClick={handleViewGalleryClick}
               viewGalleryTooltip={viewGalleryTooltip}
               openImageGallery={openImageGallery}
               setOpenImageGallery={setOpenImageGallery}
               imagesForGallery={imagesForGallery}
               allowImageMatch={isAdminCompanyUser}
               imageMatchIsTertiary={imageMatchIsTertiary}
               imageMatchIsDisabled={imageMatchIsDisabled}
               imageMatchTooltip={imageMatchTooltip}
               handleImageMatchClick={handleImageMatchClick}
               imageMatchModalOpen={imageMatchModalOpen}
               setImageMatchModalOpen={setImageMatchModalOpen}
               isImageMatchSuccess={isImageMatchSuccess}
               imageMatchErrorMessage={imageMatchErrorMessage}
               handleImageSelection={handleImageSelection}
               handleAddMatchedImages={handleAddMatchedImages}
               maxAllowedImages={maxAllowedImages}
            />
         )}

         {/* Other tab */}
         {tabStatus === TabStatus.OTHER && (
            <div className={styles.itemColumns}>
               <div
                  className={styles.columnOne}
                  style={{
                     pointerEvents: isReadOnly ? 'none' : 'auto',
                  }}
               >
                  <Other
                     activeValue={activeValue}
                     itemForm={itemForm}
                     updateForm={updateForm}
                     updateFormSelect={updateFormSelect}
                     settings={settings}
                     isQuantityOrConsumableToolType={
                        isQuantityOrConsumableToolType
                     }
                     conditions={conditions}
                  />
               </div>
            </div>
         )}
         {/* Rental tab */}
         {tabStatus === TabStatus.RENTAL && (
            <div className={styles.itemColumns}>
               <div
                  className={styles.columnOne}
                  style={{
                     pointerEvents: isReadOnly ? 'none' : 'auto',
                  }}
               >
                  <Rental
                     activeValue={activeValue}
                     itemForm={itemForm}
                     updateForm={updateForm}
                     updateFormSelect={updateFormSelect}
                     settings={settings}
                     toggleValue={toggleValue}
                     handleToggle={handleToggle}
                  />
               </div>
            </div>
         )}
         {/* Tasks tab */}
         {tabStatus === TabStatus.TASKS && (
            <div className={styles.itemColumns}>
               <Tasks
                  isEmployeeUser={isEmployeeUser}
                  itemId={itemId}
                  onActionTaskComplete={closeModalAndRefresh}
                  parentTaskListDetails={parentTaskListDetails}
               />
            </div>
         )}
         {/* Notes tab */}
         {tabStatus === TabStatus.NOTES && (
            <>
               {itemNotesData?.length === 0 && (
                  <Alert
                     message="No notes have been found."
                     severity={Severity.INFO}
                  />
               )}
               <div className={styles.note}>
                  <Notes
                     itemId={itemId}
                     toolLoanId={itemForm?.ToolLoanId}
                     itemNotesData={itemNotesData}
                     refetchTableData={refetchNoteTableData}
                     isNewItemNote={isNewItemNote}
                     setIsNewItemNote={setIsNewItemNote}
                     crudItemsParentPageView={parentPageView}
                  />
               </div>
            </>
         )}
         {/* History tab */}
         {tabStatus === TabStatus.HISTORY && (
            <div className={styles.itemColumns}>
               <History historyItems={itemHistoryData} />
            </div>
         )}
         {/* Map tab */}
         {tabStatus === TabStatus.MAP && <Map selectedItem={itemForm} />}
         {/* Modal Buttons */}
         <div className={stylesCrudForm.basicForm}>
            <Row>
               {!isReadOnly && (
                  <Col>
                     <Button
                        disabled={titleErrorMessage}
                        onClick={
                           !isReadOnly
                              ? handleSave
                              : () =>
                                   handleAddOrRemoveFromCart(
                                      itemForm.localUniqueId
                                   )
                        }
                     >
                        {crudItemsPrimaryButtonMessage}
                     </Button>
                  </Col>
               )}
               <Col>
                  <Button variant="tertiary" onClick={handleSecondaryModal}>
                     Cancel
                  </Button>
               </Col>
            </Row>
         </div>

         <Modal
            isModalVisible={isSecondaryModalVisible}
            closeModal={() => closeSecondaryModal()}
            title="Cancel"
         >
            <div className={stylesCrudForm.basicForm}>
               <p>Are you sure you wish to cancel?</p>
               <Row>
                  <Col>
                     <Button variant="primary" onClick={() => closeAllModals()}>
                        Yes
                     </Button>
                  </Col>
                  <Col>
                     <Button
                        variant="tertiary"
                        onClick={() => closeSecondaryModal()}
                     >
                        No
                     </Button>
                  </Col>
               </Row>
            </div>
         </Modal>
      </div>
   )
}

export default memo(CrudItems)
